import type { FC } from "react";

import type { StateOrName } from "@uirouter/react";
import { UIView, useCurrentStateAndParams } from "@uirouter/react";
import { ErrorBoundary } from "react-error-boundary";

import { EducationBarProminentProvider } from "app/components/educationbar/useEducationBarProminentContext";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { PageNotificationBarContainer } from "app/components/React/NotificationBarContainer/PageNotificationBarContainer";
import useStickySubNav from "app/components/sub-navigation-bar/UseStickySubNav";
import { WebsitePageAnalysisSubNav } from "app/components/sub-navigation-bar/WebsitePageAnalysisSubNav";
import { SwDemoBanner } from "app/components/sw-demo-banner/SwDemoBanner";

import { WebsiteAnalysisProvider } from "./WebsiteAnalysisContext";

const contentContainerId = "websiteAnalysisPageContentContainerId";

interface WebsiteAnalysisPageModuleFrameProps {
    filterRecentsBy: StateOrName;
}

export const WebsiteAnalysisPageModuleFrame: FC<WebsiteAnalysisPageModuleFrameProps> = ({ filterRecentsBy }) => {
    const { state, params } = useCurrentStateAndParams();
    const { SubNavWrapper, PageContentWrapper, subNavWrapperProps, pageContentWrapperProps } = useStickySubNav();

    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <ErrorFallback
                    {...props}
                    shouldLogErrorFallback
                />
            )}
            resetKeys={[state, params]}
        >
            <EducationBarProminentProvider>
                <WebsiteAnalysisProvider>
                    <div className="sw-layout-module-inner">
                        <span
                            className="u-flex-row u-flex-column"
                            style={{ flexGrow: 1, overflowX: "hidden", width: "100%" }}
                        >
                            <SubNavWrapper
                                className="sw-layout-subnav hidden-print"
                                {...subNavWrapperProps}
                            >
                                <WebsitePageAnalysisSubNav filterRecentsBy={filterRecentsBy} />
                            </SubNavWrapper>
                            <SwDemoBanner type="basic" />
                            <PageNotificationBarContainer />
                            <PageContentWrapper
                                className="sw-layout-scrollable-element sw-layout-research use-sticky-css-rendering"
                                {...pageContentWrapperProps}
                                id={contentContainerId}
                            >
                                <div className="sw-page-websiteAnalysis sw-layout-page">
                                    <section data-automation="page-frame-container">
                                        <UIView />
                                    </section>
                                </div>
                            </PageContentWrapper>
                        </span>
                    </div>
                </WebsiteAnalysisProvider>
            </EducationBarProminentProvider>
        </ErrorBoundary>
    );
};
